import { useTheme } from "@mui/material";
import { createSxStyles } from "../../common/createSxStyles";

export const useAiChatSidebarStyles = () => {
    const theme = useTheme();

    return createSxStyles({
        sidebar: {
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.customPalette.white,
            overflow: "hidden",
        },
        newChatButton: {
            margin: theme.spacing(2),
            padding: theme.spacing(1.25, 2),
            borderRadius: theme.spacing(1),
            background: "linear-gradient(90deg, #E127AA 0%, #FAC219 100%)",
            color: "white",
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 500,
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.spacing(1),
            "& .MuiSvgIcon-root": {
                fill: "white",
            },
            "&:hover": {
                filter: "brightness(0.92)",
            },
        },
        searchBox: {
            padding: theme.spacing(0, 2, 1.5, 2),
            "& .MuiInputBase-root": { fontSize: 13 },
            "& .MuiInputBase-input": { fontSize: 13 },
        },
        listLabel: {
            fontSize: 11,
            fontWeight: 500,
            color: theme.palette.text.secondary,
            padding: theme.spacing(1, 2, 0.5, 2),
            textTransform: "uppercase",
            letterSpacing: "0.05em",
        },
        list: {
            flex: 1,
            overflowY: "auto",
            padding: theme.spacing(0, 1, 1, 1),
        },
        listItem: {
            // Actions are absolutely positioned (see rowActions) so titles get the full row
            // width when not hovered, instead of being clipped to make room for invisible icons.
            position: "relative",
            padding: theme.spacing(1, 1.25),
            borderRadius: theme.spacing(1),
            cursor: "pointer",
            marginBottom: theme.spacing(0.25),
            display: "flex",
            alignItems: "flex-start",
            gap: theme.spacing(0.5),
            "&:hover": {
                backgroundColor: theme.palette.grey[100],
            },
            "&:hover .ai-chat-row-actions": {
                opacity: 1,
            },
        },
        listItemActive: {
            backgroundColor: "#FCE4F3",
            border: "1px solid #F5B6DC",
            "&:hover": {
                backgroundColor: "#FCE4F3",
            },
            // Match the action-overlay gradient to the active row's background so the icons
            // don't appear to sit on a different surface when hovered.
            "& .ai-chat-row-actions": {
                background: "linear-gradient(90deg, rgba(252, 228, 243, 0) 0%, #FCE4F3 60%, #FCE4F3 100%)",
            },
        },
        listItemText: {
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
        },
        listItemTitle: {
            fontSize: 13,
            fontWeight: 500,
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        },
        listItemDate: {
            fontSize: 11,
            color: theme.palette.text.disabled,
            marginTop: theme.spacing(0.25),
        },
        rowActions: {
            // Overlay the right edge of the row on hover instead of reserving space at rest. A
            // left-side gradient fades into the row's background so the title text disappears
            // behind the icons gracefully rather than looking abruptly clipped. The container
            // needs a generous left padding so the gradient has room to transition fully to
            // solid *before* it reaches the icons — otherwise title text remains visible
            // immediately to the left of the buttons.
            position: "absolute",
            top: 0,
            bottom: 0,
            right: 0,
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(0.25),
            opacity: 0,
            transition: "opacity 120ms ease",
            paddingLeft: theme.spacing(6),
            paddingRight: theme.spacing(0.75),
            borderTopRightRadius: theme.spacing(1),
            borderBottomRightRadius: theme.spacing(1),
            background: "linear-gradient(90deg, rgba(245, 245, 245, 0) 0%, #f5f5f5 60%, #f5f5f5 100%)",
            "& .MuiIconButton-root": {
                color: theme.palette.text.disabled,
                "& .MuiSvgIcon-root": { fill: theme.palette.text.disabled },
                "&:hover": {
                    color: theme.palette.text.primary,
                    "& .MuiSvgIcon-root": { fill: theme.palette.text.primary },
                },
            },
        },
        renameRow: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(0.5),
            padding: "1px",
        },
        renameInput: {
            flex: 1,
            minWidth: 0,
            "& .MuiInputBase-root": {
                fontSize: 13,
            },
            "& .MuiTextField-root": {
                padding: "1px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#F5B6DC",
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E127AA",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E127AA",
                borderWidth: "1px",
            },
        },
        renameConfirmButton: {
            color: "#2E7D32",
            "& .MuiSvgIcon-root": { fill: "#2E7D32" },
            "&:hover": {
                backgroundColor: "rgba(46, 125, 50, 0.08)",
            },
        },
        renameCancelButton: {
            color: theme.palette.text.disabled,
            "& .MuiSvgIcon-root": { fill: theme.palette.text.disabled },
            "&:hover": {
                color: theme.palette.text.primary,
                "& .MuiSvgIcon-root": { fill: theme.palette.text.primary },
            },
        },
        emptyState: {
            padding: theme.spacing(3, 2),
            textAlign: "center",
            color: theme.palette.text.secondary,
            fontSize: 12,
        },
    });
};
